/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable, Optional, Signal, signal, WritableSignal } from '@angular/core';

import { AnalyticsService, MixpanelAnalyticsProvider } from '@shure/cloud/shared/utils/analytics';

import { Cookiebot } from '../models/cookiebot.models';
import { addCookiebotListener, removeCookiebotListener } from '../utils/cookiebot.utils';

declare global {
	interface Window {
		Cookiebot?: Cookiebot;
	}
}

@Injectable()
export class CookiebotService {
	public readonly isCookiebotReady = signal(false);
	private readonly analyticsFacade: {
		isMixpanelInitialized: Signal<boolean>;
		isAnalyticsOptedIn: WritableSignal<boolean>;
		optIn(): void;
		optOut(): void;
	};

	/**
	 * For backwards compatibility, service will use whichever provider is provided by the application.
	 * If both are provided, MixpanelAnalyticsProvider is preferred.
	 */
	constructor(
		@Optional() analyticsService?: AnalyticsService,
		@Optional() mixpanelAnalyticsProvider?: MixpanelAnalyticsProvider
	) {
		if (mixpanelAnalyticsProvider) {
			this.analyticsFacade = {
				isMixpanelInitialized: mixpanelAnalyticsProvider.isMixpanelInitialized,
				isAnalyticsOptedIn: mixpanelAnalyticsProvider.isAnalyticsOptedIn,
				optIn: (): void => mixpanelAnalyticsProvider.optIn(),
				optOut: (): void => mixpanelAnalyticsProvider.optOut()
			};
		} else if (analyticsService) {
			this.analyticsFacade = {
				isMixpanelInitialized: analyticsService.isMixpanelInitialized,
				isAnalyticsOptedIn: analyticsService.isAnalyticsOptedIn,
				optIn: (): void => analyticsService.sendOptInEvent(),
				optOut: (): void => analyticsService.sendOptOutEvent()
			};
		} else {
			throw new Error(
				'CookiebotService requires either AnalyticsService or MixpanelAnalyticsProvider to be provided.'
			);
		}
	}

	/**
	 * Adds necessary Cookiebot listeners for analytics
	 */
	public addCookiebotListeners(): void {
		addCookiebotListener('CookiebotOnConsentReady', this.cookieBotReadyListener);
		addCookiebotListener('CookiebotOnAccept', this.cookieBotAcceptListener);
		addCookiebotListener('CookiebotOnDecline', this.cookieBotDeclineListener);
	}

	/**
	 * Removes Cookiebot listeners, to be called on component destroy
	 */
	public removeCookiebotListeners(): void {
		removeCookiebotListener('CookiebotOnConsentReady', this.cookieBotReadyListener);
		removeCookiebotListener('CookiebotOnAccept', this.cookieBotAcceptListener);
		removeCookiebotListener('CookiebotOnDecline', this.cookieBotDeclineListener);
	}

	/**
	 * Note: this is not checking whether a user has consented, only that Cookiebot is ready (Google Tag Manager does this)
	 * Occurs at page load and every time the value consent is affected (order is Ready -> Loaded -> Accept/Decline)
	 */
	public cookieBotReadyListener = (): void => {
		const marketingCookieConsent = window.Cookiebot?.consent.marketing ?? false;
		this.analyticsFacade.isAnalyticsOptedIn.set(marketingCookieConsent);
		this.isCookiebotReady.set(true);
	};

	/**
	 * Occurs at page load and thereafter whenever any category of cookies is accepted by user (for example, if user
	 * 1) accepts all three categories, then 2) removes one category--this event still fires twice)
	 */
	public cookieBotAcceptListener = (): void => {
		if (this.analyticsFacade.isMixpanelInitialized()) {
			if (!this.analyticsFacade.isAnalyticsOptedIn()) {
				this.analyticsFacade.optOut();
			} else {
				this.analyticsFacade.optIn();
			}
		}
	};

	/**
	 * Occurs at page load and thereafter whenever all cookies have been declined by user
	 */
	public cookieBotDeclineListener = (): void => {
		if (this.analyticsFacade.isMixpanelInitialized()) {
			this.analyticsFacade.optOut();
		}
	};
}
